import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { easing } from "../../lib/animations";
import { useCart } from "../../context/CartContext";
import { useDrawer } from "../../context/DrawerContext";

const NAV_LEFT = [
    { id: "shop", label: "LOOKBOOK", to: "/shop" },
    { id: "about", label: "MANIFEST", to: "/about" },
];
const NAV_RIGHT = [
    { id: "cart", label: "CART", to: "/cart" },
    { id: "account", label: "MY ACCOUNT", to: "/account" },
];
const ALL_NAV = [...NAV_LEFT, ...NAV_RIGHT];

const isActiveRoute = (pathname, to) => {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
};

const NavLinkItem = ({ item, active, onHover, onLeave, badge }) => (
    <Link
        to={item.to}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        className={active ? "is-active" : ""}
        data-testid={`nav-${item.id}`}
        style={{
            color: "#fff",
            textDecoration: "none",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 500,
            fontSize: "clamp(10px, 0.75vw, 12px)",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "10px 4px 12px",
            position: "relative",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
        }}
    >
        {item.label}
        {badge != null && badge > 0 && (
            <motion.span
                key={badge}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease: easing.primary }}
                data-testid={`nav-${item.id}-count`}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 18,
                    height: 18,
                    padding: "0 5px",
                    background: "#c1121f",
                    color: "#fff",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 10,
                    letterSpacing: "0",
                    fontWeight: 600,
                    borderRadius: 0,
                    lineHeight: 1,
                    boxShadow: "0 0 6px rgba(193, 18, 31, 0.55)",
                }}
            >
                {badge}
            </motion.span>
        )}
    </Link>
);

/* Magnetic bar — ticks that lift towards the cursor + a red marker that
   tracks the active nav link's horizontal centre */
const LanguageSelector = () => {
    const [open, setOpen] = useState(false);
    return (
        <div
            style={{ position: "relative" }}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="lang-selector"
                aria-label="Language"
                style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.45)",
                    color: "#fff",
                    cursor: "pointer",
                    padding: "6px 12px",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 11,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    lineHeight: 1,
                }}
            >
                EN
                <svg width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden="true">
                    <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1" fill="none" />
                </svg>
            </button>
            {open && (
                <div
                    role="listbox"
                    data-testid="lang-options"
                    style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        background: "rgba(12, 12, 12, 0.96)",
                        border: "1px solid rgba(255,255,255,0.22)",
                        padding: "8px 0",
                        minWidth: 96,
                        zIndex: 5,
                    }}
                >
                    {[
                        { code: "EN", active: true, soon: false },
                        { code: "PL", active: false, soon: true },
                    ].map((l) => (
                        <button
                            key={l.code}
                            type="button"
                            disabled={l.soon}
                            data-testid={`lang-option-${l.code.toLowerCase()}`}
                            onClick={() => setOpen(false)}
                            style={{
                                width: "100%",
                                background: "transparent",
                                border: "none",
                                color: l.active ? "#fff" : "rgba(255,255,255,0.45)",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.24em",
                                textTransform: "uppercase",
                                padding: "8px 14px",
                                cursor: l.soon ? "not-allowed" : "pointer",
                                textAlign: "left",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {l.code}
                            {l.soon && (
                                <span
                                    style={{
                                        fontSize: 8,
                                        letterSpacing: "0.32em",
                                        color: "rgba(255,255,255,0.35)",
                                    }}
                                >
                                    Soon
                                </span>
                            )}
                            {l.active && (
                                <span
                                    style={{
                                        width: 5,
                                        height: 5,
                                        background: "#c1121f",
                                        display: "inline-block",
                                    }}
                                />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const MagneticHeaderBar = ({ targetX }) => {
    const ref = useRef(null);
    const [mouseX, setMouseX] = useState(null);
    const [width, setWidth] = useState(0);
    const barCount = 180;

    useEffect(() => {
        const update = () => {
            if (ref.current) setWidth(ref.current.offsetWidth);
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const onMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        setMouseX(e.clientX - rect.left);
    };
    const onLeave = () => setMouseX(null);

    return (
        <div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{
                position: "relative",
                height: 14,
                pointerEvents: "auto",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                }}
            >
                {Array.from({ length: barCount }).map((_, i) => {
                    const tickX = width > 0 ? (i / (barCount - 1)) * width : 0;
                    const dist = mouseX != null ? Math.abs(tickX - mouseX) : 9999;
                    const h = Math.max(3, 14 - dist * 0.07);
                    return (
                        <span
                            key={i}
                            style={{
                                width: 1,
                                height: h,
                                background: "rgba(255,255,255,0.55)",
                                transition: "height 0.15s ease",
                            }}
                        />
                    );
                })}
            </div>
            {/* Red marker that follows the active nav link */}
            {targetX != null && width > 0 && (
                <span
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: Math.max(0, Math.min(width - 2, targetX)),
                        bottom: -3,
                        width: 2,
                        height: 12,
                        background: "#e63946",
                        boxShadow:
                            "0 0 6px rgba(230, 57, 70, 0.85), 0 0 14px rgba(230, 57, 70, 0.5)",
                        transition: "left 0.45s cubic-bezier(0.65, 0, 0.35, 1)",
                        transform: "translateX(-1px)",
                        pointerEvents: "none",
                    }}
                />
            )}
        </div>
    );
};

export const Header = ({ ready = true }) => {
    const location = useLocation();
    const [hovered, setHovered] = useState(null);
    const { open: drawerOpen, openDrawer } = useDrawer();
    const navRowRef = useRef(null);
    const linkRefs = useRef({});
    const videoRef = useRef(null);
    const [markerX, setMarkerX] = useState(null);
    const { count: cartCount } = useCart();

    // Derive active nav from the current route
    const active = ALL_NAV.find((n) => isActiveRoute(location.pathname, n.to))?.id || "";

    // Set up nav refs by id via callback
    const setLinkRef = (id) => (el) => {
        linkRefs.current[id] = el;
    };

    // Recompute red-marker X from hovered or active link's centre
    useEffect(() => {
        const targetId = hovered || active;
        const linkEl = targetId ? linkRefs.current[targetId] : null;
        const row = navRowRef.current;
        if (!linkEl || !row) {
            setMarkerX(null);
            return;
        }
        const lr = linkEl.getBoundingClientRect();
        const rr = row.getBoundingClientRect();
        setMarkerX(lr.left + lr.width / 2 - rr.left);
    }, [hovered, active, location.pathname]);

    // Centre logo video — slow loop
    useEffect(() => {
        if (!ready) return;
        const v = videoRef.current;
        if (!v) return;
        let cancelled = false;
        const start = () => {
            if (cancelled) return;
            try {
                v.playbackRate = 0.25;
            } catch (e) {
                /* ignore */
            }
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        if (v.readyState >= 1) {
            start();
        } else {
            v.addEventListener("loadedmetadata", start, { once: true });
        }
        return () => {
            cancelled = true;
            v.removeEventListener("loadedmetadata", start);
        };
    }, [ready]);

    return (
        <motion.header
            className="header"
            initial={{ y: -50, opacity: 0 }}
            animate={ready ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.9, delay: 0.3, ease: easing.primary }}
            data-testid="site-header"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                padding: "15px clamp(20px, 3vw, 40px) 4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
                background: "transparent",
                mixBlendMode: "difference",
                pointerEvents: "none",
            }}
        >
            {/* Three-column row: left nav | centred sygnet + video | right nav */}
            <div
                ref={navRowRef}
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    gap: "clamp(14px, 2.1vw, 40px)",
                    pointerEvents: drawerOpen ? "none" : "auto",
                    opacity: drawerOpen ? 0 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                <nav
                    className="header-nav proof-nav-desktop"
                    aria-label="Primary left"
                    style={{
                        justifyContent: "flex-start",
                        gap: "clamp(14px, 2.1vw, 34px)",
                    }}
                >
                    {NAV_LEFT.map((n) => (
                        <span ref={setLinkRef(n.id)} key={n.id}>
                            <NavLinkItem
                                item={n}
                                active={active === n.id}
                                onHover={() => setHovered(n.id)}
                                onLeave={() => setHovered(null)}
                            />
                        </span>
                    ))}
                </nav>

                {/* MOBILE: language selector — only visible below 768px */}
                <div
                    className="proof-nav-mobile-toggle"
                    style={{
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <LanguageSelector />
                </div>

                <Link
                    to="/"
                    className="header-logo"
                    data-testid="header-logo"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0,
                        color: "#fff",
                        textDecoration: "none",
                    }}
                >
                    <img
                        src="/assets/yk/sygnet-white.png"
                        alt="PROOF"
                        draggable={false}
                        style={{
                            height: "clamp(28px, 2.625vw, 42px)",
                            width: "auto",
                            display: "block",
                            userSelect: "none",
                        }}
                    />
                    <video
                        ref={videoRef}
                        muted
                        playsInline
                        loop
                        preload="auto"
                        data-testid="header-logo-video"
                        style={{
                            height: "clamp(19px, 1.75vw, 28px)",
                            width: "auto",
                            display: "block",
                            objectFit: "contain",
                            userSelect: "none",
                        }}
                    >
                        <source src="/assets/yk/proof-nav-video.mp4" type="video/mp4" />
                    </video>
                </Link>

                <nav
                    className="header-nav proof-nav-desktop"
                    aria-label="Primary right"
                    style={{
                        justifyContent: "flex-end",
                        gap: "clamp(14px, 2.1vw, 34px)",
                    }}
                >
                    {NAV_RIGHT.map((n) => (
                        <span ref={setLinkRef(n.id)} key={n.id}>
                            <NavLinkItem
                                item={n}
                                active={active === n.id}
                                onHover={() => setHovered(n.id)}
                                onLeave={() => setHovered(null)}
                                badge={n.id === "cart" ? cartCount : undefined}
                            />
                        </span>
                    ))}
                </nav>

                {/* MOBILE: hamburger — cart moved to MobileBottomBar */}
                <div
                    className="proof-nav-mobile-toggle"
                    style={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 14,
                    }}
                >
                    <button
                        type="button"
                        onClick={openDrawer}
                        data-testid="nav-mobile-burger"
                        aria-label="Open menu"
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            padding: "8px 4px",
                            display: "inline-flex",
                            flexDirection: "column",
                            gap: 4,
                        }}
                    >
                        <span
                            style={{
                                width: 22,
                                height: 1.5,
                                background: "currentColor",
                                display: "block",
                            }}
                        />
                        <span
                            style={{
                                width: 22,
                                height: 1.5,
                                background: "currentColor",
                                display: "block",
                            }}
                        />
                        <span
                            style={{
                                width: 16,
                                height: 1.5,
                                background: "currentColor",
                                display: "block",
                                marginLeft: "auto",
                            }}
                        />
                    </button>
                </div>
            </div>

            <MagneticHeaderBar targetX={markerX} />
        </motion.header>
    );
};

export default Header;
